import { paidLeaveTypeList } from '@/views/Leave/Leave.constants';
import firebase from '../config/firebase.config';
import { leaveBalanceService } from './leaveBalance';
import { getTeamMemberDetails } from './teamMemberDetails';

const database = firebase.firestore();

/**
 * Format leave date (YYYY-M-D or YYYY-MM-DD) to attendance keys used by attendance-consolidation.
 * formattedDate: "DD:MM:YYYY" (date key in attendanceByMonth doc)
 * formattedMonth: "january-2025" (doc id)
 */
function getAttendanceKeysFromLeaveDate(leaveDateStr) {
  const parts = String(leaveDateStr)
    .split('-')
    .map((n) => parseInt(n, 10));
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  const d = new Date(year, month - 1, day);
  const dayP = String(d.getDate()).padStart(2, '0');
  const monthP = String(d.getMonth() + 1).padStart(2, '0');
  const formattedDate = `${dayP}:${monthP}:${year}`;
  const monthName = d
    .toLocaleString('default', { month: 'long' })
    .toLowerCase();
  const formattedMonth = `${monthName}-${year}`;
  return { formattedDate, formattedMonth };
}

/**
 * Get all team IDs in the company where the user is a member.
 */
async function getTeamIdsForUserInCompany(companyId, userId) {
  const teamsSnap = await database
    .collection('teamDatabase')
    .where('ownedBy', '==', companyId)
    .get();
  const teamIds = [];
  for (const doc of teamsSnap.docs) {
    const memberDetails = await getTeamMemberDetails(database, doc.id);
    if (memberDetails[userId]) {
      teamIds.push(doc.id);
    }
  }
  return teamIds;
}

const leaveService = {
  /**
   * Get leave requests assigned to a manager (assignee).
   */
  async getManagerRequests(companyId, managerId) {
    const snap = await database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('leaveRequests')
      .where('assignee', '==', managerId)
      .get();
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  /**
   * Reject a leave request: only update status to "Rejected".
   */
  async rejectLeaveRequest(companyId, requestId) {
    const ref = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('leaveRequests')
      .doc(requestId);
    await ref.update({ status: 'Rejected' });
  },

  /**
   * Approve a leave request: update status and write leave to attendance in
   * membersDatabase and teamDatabase (attendanceByMonth), matching attendance-consolidation structure.
   */
  async approveLeaveRequest(companyId, requestId, requestData) {
    const { userId, leaveDate, type, teamId: requestTeamId } = requestData;
    const leaveType = type || 'Leave';

    const ref = database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('leaveRequests')
      .doc(requestId);

    const { formattedDate, formattedMonth } =
      getAttendanceKeysFromLeaveDate(leaveDate);
    const dayData = {
      present: false,
      startTime: null,
      endTime: null,
      leave: true,
      leaveType,
    };

    // 1. Update leave request status
    await ref.update({ status: 'Approved' });

    // 2. Deduct paid leave from user's usage (so balance is correct)
    if (paidLeaveTypeList.includes(leaveType)) {
      const year = String(leaveDate).split('-')[0];
      await leaveBalanceService.deductPaidLeave(
        userId,
        companyId,
        leaveType,
        year
      );
    }

    // 3. membersDatabase: membersDatabase/{userId}/attendanceByMonth/{formattedMonth}
    const membersMonthRef = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('attendanceByMonth')
      .doc(formattedMonth);
    await membersMonthRef.set({ [formattedDate]: dayData }, { merge: true });

    // 4. teamDatabase: for the request's teamId if present, else all teams user is in
    let teamIds = [];
    if (requestTeamId) {
      teamIds = [requestTeamId];
    } else {
      teamIds = await getTeamIdsForUserInCompany(companyId, userId);
    }
    for (const teamId of teamIds) {
      const teamMemberMonthRef = database
        .collection('teamDatabase')
        .doc(teamId)
        .collection('members')
        .doc(userId)
        .collection('attendanceByMonth')
        .doc(formattedMonth);
      await teamMemberMonthRef.set(
        { [formattedDate]: dayData },
        { merge: true }
      );
    }
  },
};

export default leaveService;
